import { useEffect, useMemo, useState } from "react";
import { Drawer, Segmented, Table, Typography } from "antd";
import { useTheme } from "../../../contexts/ThemeContext";
import SynchronizationService from "../../../services/synchronizationService";
import { TIPO_TITULO, TIPO_VENDA, formatDate, formatMoney, formatNumber } from "./additionalInfoUtils";

const { Text } = Typography;
const PAGE_SIZE = 20;

/** Lançamentos ativos, paginados no servidor. Carrega só quando aberto. */
const EntriesDrawer = ({ open, onClose, syncId, supportsSales, supportsDepartments, refreshKey }) => {
  const { theme } = useTheme();
  const [tipo, setTipo] = useState(TIPO_TITULO);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ itens: [], total: 0 });

  useEffect(() => {
    if (!open || !syncId) return undefined;
    let cancelled = false;
    setLoading(true);
    SynchronizationService.getInfoAdicional(syncId, { tipo, page, pageSize: PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setData({ itens: res?.itens || [], total: res?.total ?? 0 });
      })
      .catch(() => {
        // O interceptor já exibe o erro.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, syncId, tipo, page, refreshKey]);

  const secondary = (value) => <Text style={{ fontSize: 12, color: theme.textSecondary }}>{value}</Text>;
  const naoDefinido = (value) => value || secondary("Não definido");

  const tituloColumns = useMemo(
    () => [
      { title: "Descrição", dataIndex: "descricao", ellipsis: true },
      { title: "Pessoa", dataIndex: "pessoa", ellipsis: true, render: naoDefinido },
      { title: "Categoria", dataIndex: "categoria", ellipsis: true },
      ...(supportsDepartments
        ? [
            {
              title: "Departamento",
              dataIndex: "departamento",
              ellipsis: true,
              render: (value) => (!value || value === "1" ? secondary("Não definido") : value),
            },
          ]
        : []),
      // Sempre resolvida no upload: a da categoria do ERP, ou a informada para categoria nova.
      { title: "Natureza", dataIndex: "natureza", width: 95, render: (v) => (v === "P" ? "Pagar" : v === "R" ? "Receber" : "") },
      { title: "Emissão", dataIndex: "dataEmissao", width: 105, render: formatDate },
      { title: "Vencimento", dataIndex: "dataVencimento", width: 110, render: formatDate },
      { title: "Pagamento", dataIndex: "dataPagamento", width: 110, render: (v) => formatDate(v) || secondary("—") },
      { title: "Valor", dataIndex: "valor", width: 130, align: "right", render: formatMoney },
      { title: "Planilha", dataIndex: "nomeArquivo", width: 170, ellipsis: true, render: secondary },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportsDepartments, theme],
  );

  const vendaColumns = useMemo(
    () => [
      { title: "Descrição", dataIndex: "descricao", ellipsis: true },
      { title: "Cliente", dataIndex: "pessoa", ellipsis: true, render: naoDefinido },
      { title: "Produto", dataIndex: "produto", ellipsis: true },
      { title: "Emissão", dataIndex: "dataEmissao", width: 105, render: formatDate },
      { title: "Qtd.", dataIndex: "quantidade", width: 90, align: "right", render: formatNumber },
      { title: "Valor unit.", dataIndex: "valorUnitario", width: 120, align: "right", render: formatMoney },
      {
        title: "Total",
        key: "total",
        width: 130,
        align: "right",
        render: (_, row) => formatMoney((row.quantidade ?? 0) * (row.valorUnitario ?? 0)),
      },
      { title: "Planilha", dataIndex: "nomeArquivo", width: 170, ellipsis: true, render: secondary },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  );

  return (
    <Drawer
      title="Lançamentos enviados"
      open={open}
      onClose={onClose}
      width="min(1100px, 100vw)"
      destroyOnHidden
      styles={{ body: { paddingTop: 12 } }}
    >
      {supportsSales && (
        <Segmented
          value={tipo}
          onChange={(value) => {
            setTipo(value);
            setPage(1);
          }}
          options={[
            { label: "Títulos", value: TIPO_TITULO },
            { label: "Vendas", value: TIPO_VENDA },
          ]}
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={tipo === TIPO_VENDA ? vendaColumns : tituloColumns}
        dataSource={data.itens}
        scroll={{ x: 1200 }}
        locale={{ emptyText: "Nenhum lançamento" }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data.total,
          showSizeChanger: false,
          size: "small",
          onChange: setPage,
        }}
      />
    </Drawer>
  );
};

export default EntriesDrawer;
